import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Table } from 'antd'

import { defaultFilter } from 'core/application/commons/defaultOrderableValues'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import {
  PagedEmployees,
  finishSignup,
  getEmployees,
  getTemplateurl,
  uploadCensusTemplate,
} from 'core/application/ichraDecisionTool/prospectSignup/step3/question1'

import Card from 'common/components/dataDisplay/Card'
import FileUpload from 'common/components/dataEntry/components/FileUpload'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import Icon from 'common/components/general/Icon'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import { commandRequest, queryRequest } from 'common/RequestUtils'
import { ROUTE_PATHS } from 'common/routePaths'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import { SIGNUP_COMPLETE_PATH } from '../SignupComplete'
import ConfirmFinishSignup from './ConfirmFinishSignup'
import { employesColumns } from './EmployeesTableColumns'
import UploadCensusInstructions from './UploadCensusInstructions'

const { ICHRA_DECISION_TOOL_PATH } = ROUTE_PATHS

export interface Step3Question1Props {
  companyName: string
}

const Question1: React.FC<BaseNavigationProps<Step3Question1Props>> = (props) => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [confirmFinishApplication, setConfirmFinishApplication] = useState<boolean>(false)
  const [employees, setEmployees] = useState<PagedEmployees | null>(null)
  const [uploading, setUploading] = useState<boolean>(false)
  const [finishing, setFinishing] = useState<boolean>(false)
  const [employeesFilter, setEmployeesFilter] = useState<OrderedPageFilter>({
    ...defaultFilter,
    orderColumn: 'identifier',
  })
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false)
  const history = useHistory()

  useEffect(() => {
    fetchEmployees()
  }, [])

  useEffect(() => {
    updateEmployeesTable()
  }, [employeesFilter])

  const downloadCensusTemplate = async () => {
    const result = await queryRequest(() => getTemplateurl())
    window.open(result)
  }

  const onEmployeesTableChange = (pagination: any, filters: any, sorter: any) => {
    setEmployeesFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'identifier',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const onFileChoosed = async (file: File) => {
    setUploading(true)
    const res = await commandRequest(() => uploadCensusTemplate(file))
    if (!res.errors) {
      notification.open({
        message: translate('file-uploaded'),
        type: 'success',
      })
      await fetchEmployees()
    } else {
      setEmployees(null)
      notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
    setUploading(false)
  }

  const fetchEmployees = async () => {
    const res = await queryRequest(() => getEmployees(employeesFilter))
    if (!res.errors) {
      setEmployees(res)
    } else {
      console.log(res.errors)
      notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
  }

  const updateEmployeesTable = async () => {
    setLoadingEmployees(true)
    const result = await queryRequest(() => getEmployees(employeesFilter))
    setEmployees(result)
    setLoadingEmployees(false)
  }

  const finishProspectSignup = async () => {
    setFinishing(true)
    const res = await commandRequest(() => finishSignup())
    if (!res.errors) {
      history.push(`${ICHRA_DECISION_TOOL_PATH}/${SIGNUP_COMPLETE_PATH}/${props.state.companyName}`)
    } else {
      console.log(res.errors)
      notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
    setFinishing(false)
  }

  const hasUploadedEmployees = employees && employees.items.length > 0

  const employeesTitle = (
    <div className="table_title_content">
      <h5>
        {translate('prospect-signup.step-3.your-employees')} {`(${employees && employees.totalItemCount})`}
      </h5>
    </div>
  )

  return (
    <>
      <Row>
        <Col xs={12}>
          <CategoryItemPageHeader
            title={translate('prospect-signup.step-3.question-1-title')}
            numberOfQuestions={1}
            currentQuestion={1}
            question={translate('prospect-signup.step-3.question-1')}
            className="ml-0"
            titleFontSize={20}
          />
        </Col>
      </Row>

      <Row>
        <Col md={18}>
          <span className="color-gray-8">{translate('prospect-signup.step-3.download-template-description')}</span>
          <br />
          <Button customIcon={<Icon type="download" />} className="mt-8" onClick={downloadCensusTemplate}>
            {translate('prospect-signup.step-3.download-template')}
          </Button>
        </Col>
        <Col md={18} className="mt-16">
          <span className="color-gray-8">{translate('prospect-signup.step-3.upload-census-description')}</span> <br />
          <UploadCensusInstructions translate={translate} />
          <br />
          <div>
            <FileUpload
              uploadTitle={translate('prospect-signup.step-3.upload-census')}
              onFileChoosed={onFileChoosed}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              uploading={uploading}
            />
          </div>
        </Col>
      </Row>

      {employees && hasUploadedEmployees && (
        <Row>
          <Col className="mt-24">
            <Card title={employeesTitle}>
              <Table
                dataSource={employees.items}
                onChange={onEmployeesTableChange}
                loading={loadingEmployees}
                columns={employesColumns(
                  employeesFilter,
                  () => {
                    updateEmployeesTable()
                  },
                  translate,
                )}
                pagination={{
                  total: employees.totalItemCount,
                  pageSize: employees.pageSize,
                  current: employees.pageNumber,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '30', '40'],
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div className="mt-32">
        <Col xs={24}>
          <div className="bottom__row">
            <Button
              icon="fa fa-arrow-left"
              size="large"
              htmlType="button"
              type="default"
              onClick={() => {
                props.goBack(props.name)
              }}
            >
              {translate('back')}
            </Button>

            {hasUploadedEmployees && (
              <Button
                icon="fa fa-arrow-right"
                iconPosition="right"
                size="large"
                htmlType="submit"
                type="primary"
                className="float-right next__button"
                onClick={() => setConfirmFinishApplication(true)}
              >
                {translate('finish')}
              </Button>
            )}
          </div>
        </Col>
      </div>

      <ConfirmFinishSignup
        visible={confirmFinishApplication}
        headerTitle={translate('prospect-signup.step-3.finish-signup')}
        content={translate('prospect-signup.step-3.finish-signup-body')}
        handleCancel={() => setConfirmFinishApplication(false)}
        handleOk={() => finishProspectSignup()}
        loading={finishing}
      />
    </>
  )
}

export default Question1
